import { jsx as _jsx } from "react/jsx-runtime";
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useContactActions } from '~/hooks/useContactActions';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, errorTextsPath } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
export const GetContactEmailChangeGuardTID = (parentTestId)=>getTestIds(parentTestId);
export function useContactEmailChangeGuard(contact, config) {
    const { biPrefix, testIds } = config;
    const overlayContext = useOverlayContext();
    const { fetchContactProposals, withdrawToEditContactProposals } = useContactActions();
    const { t: manageContactModalForVendorTexts } = useLocaleKeys('components.manageContactModalForVendor');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const openUpdateAnchorUserEmailModal = ()=>overlayContext.showModal({
            biName: "".concat(biPrefix, "_modal_changes_to_existing_confirm"),
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: "".concat(biPrefix, "_update_email")
                        },
                        secondaryButton: {
                            name: "".concat(biPrefix, "_update_email_cancel")
                        }
                    },
                    skin: "danger",
                    title: manageContactModalForVendorTexts.changesToExistingTitle(),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: manageContactModalForVendorTexts.updateEmailDescription()
                    }),
                    textAlignCenter: false,
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    primaryButtonLabel: manageContactModalForVendorTexts.proceedAnyway(),
                    secondaryButtonLabel: commonButtons.cancel(),
                    primaryButtonTestId: testIds.updateEmailConfirmButton,
                    secondaryButtonTestId: testIds.updateEmailCancelButton,
                    testId: testIds.updateEmailModal
                });
            }
        });
    const openWithdrawToProposalModal = ()=>overlayContext.showModal({
            biName: "".concat(biPrefix, "_modal_proposals_awaiting_confirm"),
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: "".concat(biPrefix, "_withdraw_proposals")
                        },
                        secondaryButton: {
                            name: "".concat(biPrefix, "_withdraw_proposals_cancel")
                        }
                    },
                    skin: "danger",
                    title: manageContactModalForVendorTexts.proposalsAwaitingTitle(),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: manageContactModalForVendorTexts.withdrawProposalsDescription()
                    }),
                    textAlignCenter: false,
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    primaryButtonLabel: manageContactModalForVendorTexts.saveChangesAndWithdraw(),
                    secondaryButtonLabel: commonButtons.back(),
                    primaryButtonTestId: testIds.withdrawConfirmButton,
                    secondaryButtonTestId: testIds.withdrawCancelButton,
                    testId: testIds.withdrawModal
                });
            }
        });
    const guard = async (newEmail)=>{
        const hasEmailChanged = newEmail !== contact.email();
        if (!hasEmailChanged) return 'proceed';
        const userId = contact.userId();
        if (userId != null) {
            const shouldProceed = await openUpdateAnchorUserEmailModal();
            if (!shouldProceed) return 'cancel';
        }
        const contactProposalsResult = await handlePromise(fetchContactProposals(contact.id()));
        if (!contactProposalsResult.success) {
            overlayContext.showErrorToast(new ErrorResponse(contactProposalsResult.error).saveContactErrorText(errorTexts), 'save-contact-error-toast');
            return 'cancel';
        }
        if (contactProposalsResult.data.length > 0) {
            const shouldWithdrawProposals = await openWithdrawToProposalModal();
            if (!shouldWithdrawProposals) return 'cancel';
            await withdrawToEditContactProposals(contact.id());
        }
        return 'proceed';
    };
    return {
        guard
    };
}
